//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ApiClient, backgroundPollHeaders } from './apiClient';
import {
  type ConfigXgt, type ConfigXgtUpdate, type MemoryData, type UserProfile
} from './apiTypes';

/**
 * Config and profile: xGT memory/config reads and writes, user-profile
 * read/update, and the ODBC connection test.
 *
 * `getAppConfig` is not here — it gates on `restoreSession`, so it lives on
 * `AuthService` with the rest of the session lifecycle.
 */
export class ConfigService {
  constructor(private readonly api: ApiClient) {}

  getMemory = (background = false): Promise<MemoryData> => {
    return this.api.makeRequest<MemoryData>(
      'memory', 'get', null, false, null, backgroundPollHeaders(background));
  };

  getConfigXgt = (): Promise<ConfigXgt> => {
    return this.api.makeRequest<ConfigXgt>('config/xgt', 'get');
  };

  // Sets settings in the xgt server config and returns the authoritative
  // post-write config (same shape as getConfigXgt) so callers can seed their
  // cache without a follow-up GET.
  setConfigXgt = (newSettings: ConfigXgtUpdate): Promise<ConfigXgt> => {
    return this.api.makeRequest<ConfigXgt>('config/xgt', 'put', newSettings);
  };

  getUserProfile = (): Promise<UserProfile> => {
    return this.api.makeRequest<UserProfile>('profile', 'get');
  };

  updateUserProfile = async (profile: Partial<UserProfile>): Promise<void> => {
    await this.api.makeRequest('profile', 'put', profile);
  };

  testODBC = async (
    connectionString: string, index: number, modified: boolean
  ): Promise<void> => {
    await this.api.makeRequest('odbc/test', 'post', {
      connectionString, index, modified
    });
  };
}
